import React, {FunctionComponent, useCallback} from 'react'
import styles from './ObjectBody.module.sass'
import clsx from 'clsx'
import TaskGroup from '../../../../etc/TaskGroup/TaskGroup'
import {projectDetailActions} from '../../../../../redux/pages/project-detail'
import {ID} from '../../../../../types/UtilityTypes'
import TaskTabs from '../../../../navigation/TaskTabs/TaskTabs'
import {PERMISSIONS} from '../../../../../constants/permissions'
import TaskEdit from '../tasks/TaskEdit/TaskEdit'
import {taskActions} from '../../../../../redux/data/task/task-actions'
import TaskRequest from '../tasks/TaskRequest/TaskRequest'
import Button, {Size} from '../../../../controls/Button/Button'
import PlusIcon from '../../../../../../files/plus.svg'
import TagEditor from '../../../../data-components/tags/TagEditor/TagEditor'
import {motion} from 'framer-motion'
import {useAllPermissions, useRootDispatch, useRootSelector} from '../../../../../utils/hooks'
import {objectSelector, parentsSelector, tagsSelector} from '../../../../../utils/selectors'
import {EMPTY_PROJECT_WORK_OBJECT} from '../../../../../responses/objects'
import {tagActions} from '../../../../../redux/data/tag'
import {Tag} from '../../../../../responses/tags'
import TaskMobile from '../tasks/TaskMobile/TaskMobile'
import {useQueries} from '../../../../../utils/hooks/useQueries'
import {EMPTY_TASK} from '../../../../../responses/tasks'
import {TaskEditState} from '../../../../../redux/data/task/task-reducer'

interface Props {
    objectId: ID,
}

const ObjectBody: FunctionComponent<Props> = ({objectId}) => {
    const dispatch = useRootDispatch()

    const parents = useRootSelector(parentsSelector(objectId))
    const tags = useRootSelector(tagsSelector(objectId))
    const editState = useRootSelector(state => state.data.task.editState)
    const openTasks = useRootSelector(state => state.pages.projectDetail.openTasks)
    const open = useRootSelector(state => state.pages.projectDetail.openObjects[objectId] ?? false)
    const object = useRootSelector(objectSelector(objectId)) ?? EMPTY_PROJECT_WORK_OBJECT

    const canOpen = useAllPermissions([PERMISSIONS.TASK.OPEN])
    const canRequest = useAllPermissions([PERMISSIONS.TASK.REQUEST])
    const canEditTags = useAllPermissions([PERMISSIONS.PROJECT.EDIT])

    const {isMobile} = useQueries()

    const onTagAdd = useCallback((tag: string) =>
        dispatch(tagActions.add.request({objectId, tag})), [dispatch, objectId]
    )
    const onTagDelete = useCallback((tag: Tag) =>
        dispatch(tagActions.delete.request({objectId, tag})), [dispatch, objectId]
    )

    return <motion.div
        initial={{height: 0}}
        animate={{height: open ? 'auto' : 0}}
        className={clsx(styles.body, {[styles.open]: open})}
    >
        {
            parents.map(parentId => isMobile? <TaskMobile
                key={parentId}
                parentId={parentId}
                objectId={objectId}
                projectId={object.project_id}
                taskId={openTasks[parentId] ?? parentId}
                onChange={taskId => dispatch(projectDetailActions.openTask({taskId, parentId}))}
            />:<TaskGroup
                key={parentId}
                parentId={parentId}
                objectId={objectId}
                projectId={object.project_id}
                taskId={openTasks[parentId] ?? parentId}
                onChange={taskId => dispatch(projectDetailActions.openTask({parentId, taskId}))}
            />)
        }
        <div className={styles.bottom}>
            {
                (TaskEditState.is.create(editState) && editState.objectId === objectId) ?
                    <div className={styles.newTask}>
                        <div className={styles.tabs}>
                            <TaskTabs
                                append
                                items={[]}
                                activeId={EMPTY_TASK.id}
                            />
                        </div>
                        <div/>
                        {
                            canOpen ?
                                <TaskEdit
                                    className={styles.content}
                                    projectId={object.project_id}
                                    actionText={'Создать'}
                                    onCancel={() => dispatch(taskActions.cancelEdit())}
                                    onSuccess={task => dispatch(taskActions.create.request({objectId, task}))}
                                /> :
                                <TaskRequest
                                    edit
                                    className={styles.content}
                                    onOk={task => dispatch(taskActions.create.request({objectId, task}))}
                                    onCancel={() => dispatch(taskActions.cancelEdit())}
                                />
                        }
                    </div> : (canRequest || canOpen) &&
                    <div>
                        <Button size={Size.BIG_ICON} onClick={() => dispatch(taskActions.startCreate({objectId}))}>
                            <PlusIcon className={styles.borderPlusIcon} /> Добавить линию задач
                        </Button>
                    </div>
            }
            <TagEditor
                tags={tags}
                constantTags={object.work_object_type.work_object.tags}
                onAdd={onTagAdd}
                onDelete={onTagDelete}
                editable={canEditTags}
            />
        </div>
    </motion.div>
}

export default ObjectBody